import { z } from "zod";
import { DbUpdate, zodCreateInputOmit, zodUpdateInputOmit } from "../../helpers";
import { OmitStrict, SupernovaException } from "../../utils";
import {
  DesignElementBase,
  DesignElementBrandedPart,
  DesignElementGroupableRequiredPart,
  DesignElementOrigin,
} from "./base";
import {
  BlurTokenData,
  BorderRadiusTokenData,
  BorderTokenData,
  BorderWidthTokenData,
  ColorTokenData,
  DimensionTokenData,
  DurationTokenData,
  FontFamilyTokenData,
  FontSizeTokenData,
  FontTokenData,
  FontWeightTokenData,
  GradientTokenData,
  LetterSpacingTokenData,
  LineHeightTokenData,
  OpacityTokenData,
  ParagraphSpacingTokenData,
  ProductCopyTokenData,
  ShadowTokenData,
  SizeTokenData,
  SpaceTokenData,
  StringTokenData,
  TextCaseTokenData,
  TextDecorationTokenData,
  TypographyTokenData,
  VisibilityTokenData,
  ZIndexTokenData,
} from "./data";
import { areTokenTypesCompatible, castStringToDimensionValue, DesignTokenType } from "./raw-element";

//
// Base
//

export const DesignTokenOriginPart = z.object({
  referenceOriginId: z.string().optional(),
  referenceOriginKey: z.string().optional(),
  referenceOriginName: z.string().optional(),
  referenceOriginRemote: z.boolean().optional(),
  referencePersistentId: z.string().optional(),
  referenceResolutionFailed: z.boolean().optional(),
  key: z.string().optional(),
  sortOrder: z.number().optional(),
});

export const DesignTokenOrigin = DesignElementOrigin.extend(DesignTokenOriginPart.shape);

const DesignTokenBase = DesignElementBase.extend(DesignElementGroupableRequiredPart.shape)
  .extend(DesignElementBrandedPart.shape)
  .extend({
    origin: DesignTokenOrigin.optional(),
    collectionPersistentId: z.string().optional(),
  });

const CreateDesignTokenBase = DesignTokenBase.omit(zodCreateInputOmit());
const UpdateDesignTokenBase = DesignTokenBase.omit({
  ...zodUpdateInputOmit(),
  brandPersistentId: true,
  designSystemVersionId: true,
});

//
// Per type
//

const BlurTokenTypedData = z.object({
  type: z.literal("Blur"),
  data: BlurTokenData,
});

const ColorTokenTypedData = z.object({
  type: z.literal("Color"),
  data: ColorTokenData,
});

const GradientTokenTypedData = z.object({
  type: z.literal("Gradient"),
  data: GradientTokenData,
});

const OpacityTokenTypedData = z.object({
  type: z.literal("Opacity"),
  data: OpacityTokenData,
});

const ShadowTokenTypedData = z.object({
  type: z.literal("Shadow"),
  data: ShadowTokenData,
});

const TypographyTokenTypedData = z.object({
  type: z.literal("Typography"),
  data: TypographyTokenData,
});

const StringTokenTypedData = z.object({
  type: z.literal("String"),
  data: StringTokenData,
});

const DimensionTokenTypedData = z.object({
  type: z.literal("Dimension"),
  data: DimensionTokenData,
});

const FontSizeTokenTypedData = z.object({
  type: z.literal("FontSize"),
  data: FontSizeTokenData,
});

const FontFamilyTokenTypedData = z.object({
  type: z.literal("FontFamily"),
  data: FontFamilyTokenData,
});

const FontWeightTokenTypedData = z.object({
  type: z.literal("FontWeight"),
  data: FontWeightTokenData,
});

const LetterSpacingTokenTypedData = z.object({
  type: z.literal("LetterSpacing"),
  data: LetterSpacingTokenData,
});

const LineHeightTokenTypedData = z.object({
  type: z.literal("LineHeight"),
  data: LineHeightTokenData,
});

const ParagraphSpacingTokenTypedData = z.object({
  type: z.literal("ParagraphSpacing"),
  data: ParagraphSpacingTokenData,
});

const TextCaseTokenTypedData = z.object({
  type: z.literal("TextCase"),
  data: TextCaseTokenData,
});

const TextDecorationTokenTypedData = z.object({
  type: z.literal("TextDecoration"),
  data: TextDecorationTokenData,
});

const BorderRadiusTokenTypedData = z.object({
  type: z.literal("BorderRadius"),
  data: BorderRadiusTokenData,
});

const BorderWidthTokenTypedData = z.object({
  type: z.literal("BorderWidth"),
  data: BorderWidthTokenData,
});

const BorderTypedData = z.object({
  type: z.literal("Border"),
  data: BorderTokenData,
});

const ProductCopyTypedData = z.object({
  type: z.literal("ProductCopy"),
  data: ProductCopyTokenData,
});

const SizeTypedData = z.object({
  type: z.literal("Size"),
  data: SizeTokenData,
});

const SpaceTypedData = z.object({
  type: z.literal("Space"),
  data: SpaceTokenData,
});

const VisibilityTypedData = z.object({
  type: z.literal("Visibility"),
  data: VisibilityTokenData,
});

const ZIndexTypedData = z.object({
  type: z.literal("ZIndex"),
  data: ZIndexTokenData,
});

const DurationTypedData = z.object({
  type: z.literal("Duration"),
  data: DurationTokenData,
});

const FontTypedData = z.object({
  type: z.literal("Font"),
  data: FontTokenData,
});

//
// Global types
//

export const DesignTokenTypedData = z.discriminatedUnion("type", [
  BlurTokenTypedData,
  BorderRadiusTokenTypedData,
  BorderWidthTokenTypedData,
  BorderTypedData,
  ColorTokenTypedData,

  DimensionTokenTypedData,
  DurationTypedData,
  FontSizeTokenTypedData,
  FontFamilyTokenTypedData,
  FontWeightTokenTypedData,

  GradientTokenTypedData,
  LetterSpacingTokenTypedData,
  LineHeightTokenTypedData,
  OpacityTokenTypedData,
  ParagraphSpacingTokenTypedData,

  ProductCopyTypedData,
  ShadowTokenTypedData,
  SizeTypedData,
  SpaceTypedData,
  StringTokenTypedData,

  TextCaseTokenTypedData,
  TextDecorationTokenTypedData,
  TypographyTokenTypedData,
  VisibilityTypedData,
  ZIndexTypedData,
  FontTypedData,
]);

export type DesignTokenOriginPart = z.infer<typeof DesignTokenOriginPart>;
export type DesignTokenOrigin = z.infer<typeof DesignTokenOrigin>;

export type DesignTokenBase = z.infer<typeof DesignTokenBase>;

export const DesignToken = DesignTokenTypedData.and(DesignTokenBase);
export const CreateDesignToken = DesignTokenTypedData.and(CreateDesignTokenBase);

export type DesignTokenTypedData = z.infer<typeof DesignTokenTypedData>;
export type DesignTokenTypedDataOfType<T extends DesignTokenType> = Extract<DesignTokenTypedData, { type: T }>;

export type DesignToken = z.infer<typeof DesignToken>;
export type ImportedDesignToken = OmitStrict<DesignToken, "origin"> & { origin: DesignTokenOrigin };

export type DesignTokenOfType<T extends DesignTokenType> = Extract<DesignToken, { type: T }>;
export type ImportedDesignTokenOfType<T extends DesignTokenType> = Extract<ImportedDesignToken, { type: T }>;

export type CreateDesignToken = z.infer<typeof CreateDesignToken>;
export type UpdateDesignToken = OmitStrict<DbUpdate<DesignToken>, "brandPersistentId" | "designSystemVersionId">;

export type DesignTokensDiff = {
  toCreate: CreateDesignToken[];
  toUpdate: UpdateDesignToken[];
};

//
// Utilities
//

export function extractTokenTypedData<T extends DesignTokenType, I extends DesignTokenTypedDataOfType<T>>(
  source: I
): DesignTokenTypedDataOfType<T> {
  return {
    type: source.type,
    data: source.data,
  } as DesignTokenTypedDataOfType<T>;
}

export function convertTokenTypedData<I extends DesignTokenType, O extends DesignTokenType>(
  source: DesignTokenTypedDataOfType<I>,
  type: O,
  isNonCompatibleTypeChangesEnabled: boolean
): DesignTokenTypedDataOfType<O> {
  if (!areTokenTypesCompatible(source.type, type, isNonCompatibleTypeChangesEnabled)) {
    throw SupernovaException.invalidOperation(`Cannot convert token from ${source.type} to ${type}`);
  }

  const data = source.data;
  if (isNonCompatibleTypeChangesEnabled) {
    data.value = castStringToDimensionValue(source.type, type, source.data.value);
  }

  return {
    type,
    data,
  } as DesignTokenTypedDataOfType<O>;
}

export function isImportedDesignToken<T extends DesignTokenType>(
  designToken: DesignTokenOfType<T>
): designToken is ImportedDesignTokenOfType<T> {
  return !!designToken.origin;
}

export function isDesignTokenOfType<T extends DesignTokenType>(
  designToken: DesignToken,
  type: T
): designToken is DesignTokenOfType<T> {
  return designToken.type === type;
}

export function designTokenTypeFilter<T extends DesignTokenType>(
  type: T
): (designToken: DesignToken) => designToken is DesignTokenOfType<T> {
  return (designToken): designToken is DesignTokenOfType<T> => isDesignTokenOfType(designToken, type);
}
